<template>
  <div
    class="kds-banner"
    :class="`kds-banner--${resolvedTone}`"
    role="alert"
    aria-live="polite"
  >
    <KdsIcon
      v-if="showIcon"
      class="kds-banner__icon"
      :name="iconName"
      :size="18"
      aria-hidden="true"
    />
    <span class="kds-banner__content">
      <slot />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import KdsIcon from './KdsIcon.vue';
import type { KdsBannerTone, KdsIconName } from '@/kds/types';

interface Props {
  tone?: KdsBannerTone;
  /** When false, hides the leading icon. Default true. */
  icon?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'info',
  icon: true
});

const KNOWN: KdsBannerTone[] = ['danger', 'warning', 'info'];

// Fallback safety: unknown tone → info (the most neutral signal).
const resolvedTone = computed<KdsBannerTone>(() =>
  KNOWN.includes(props.tone) ? props.tone : 'info'
);

const showIcon = computed(() => props.icon);

const iconName = computed<KdsIconName>(() => 'triangle-alert');
</script>

<style scoped lang="scss">
.kds-banner {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--r-3);
  border: var(--bw) solid transparent;
  font-family: var(--font-ui);
  font-size: var(--fs-body-sm);
  line-height: var(--lh-body);

  /*
   * Tints use `color-mix` against the canonical semantic tokens
   * (--danger, --warn, --info) so the banner re-tones automatically
   * when the theme flips light/dark — no hardcoded rgba(...).
   */
  &--danger {
    background: color-mix(in srgb, var(--danger) 10%, var(--paper));
    border-color: color-mix(in srgb, var(--danger) 30%, transparent);
    color: var(--danger);
  }

  &--warning {
    background: color-mix(in srgb, var(--warn) 12%, var(--paper));
    border-color: color-mix(in srgb, var(--warn) 30%, transparent);
    color: var(--warn);
  }

  &--info {
    background: color-mix(in srgb, var(--info) 10%, var(--paper));
    border-color: color-mix(in srgb, var(--info) 28%, transparent);
    color: var(--info);
  }

  &__icon {
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__content {
    flex: 1;
    color: var(--ink);
  }
}
</style>
